import React, { HTMLAttributes, useCallback, useEffect, useState } from "react";

import { IonImg } from "@ionic/react";

import classNames from "classnames/bind";
import styles from "./Image.module.scss";
import { getAppConfig } from "@core/config";
import Images from "@core/images";
import { ImageType, ProductImageTypes } from "@core/types";
import { useConfig } from "../../providers/config";

type ProductThumbnailImageProps = {
  src?: HTMLIonImgElement["src"];
  image?: ImageType;
  alt?: HTMLIonImgElement["alt"];
  downsize?: boolean;
  lazyLoad?: boolean;
  placeholderClassName?: string;
} & HTMLAttributes<HTMLImageElement> &
  HTMLAttributes<HTMLIonImgElement>;

const config = getAppConfig();

const cx = classNames.bind(styles);
const changeHost = (src: string): string => {
  const CDN_DOMAIN = config.CDNDomain;
  if (!CDN_DOMAIN) return src;
  try {
    const url = new URL(src);
    url.host = CDN_DOMAIN;
    return url.href;
  } catch {
    return src;
  }
};

const processImgSrc = (src: string, resize: boolean = true): string => {
  const dotSymbolIndex = src.lastIndexOf(".");
  const resized = resize ? src.substring(0, dotSymbolIndex) + "-300x300" + src.substring(dotSymbolIndex) : src;
  return changeHost(resized);
};

const defaultPlaceholder = Images.placeholderLogo;

const Image = ({
  image,
  src = image?.src ?? "",
  className,
  downsize = getAppConfig().image.isDownSizeEnabled,
  lazyLoad = getAppConfig().image.isLazyLoadingEnabled,
  placeholder = defaultPlaceholder,
  placeholderClassName,
  ...imgProps
}: ProductThumbnailImageProps) => {
  const imageExtension = config.image.base64Extension;
  const isNeedBase65Prefix = config.image.isNeedBase65Prefix;
  const isBase64InSrc = config.image.isBase64InSrc;

  const handleImgError = useCallback(
    (e: any) => {
      e.currentTarget.src = placeholder;
    },
    [src, image],
  );

  const [imageUrl, setImageUrl] = useState(processImgSrc(placeholder!, false));

  const imageType = image ? image.type : ProductImageTypes.img;

  useEffect(() => {
    if (imageType === ProductImageTypes.base64) {
      return;
    }

    let isMounted = true;
    const img = new window.Image();
    img.onload = function () {
      if (isMounted) {
        setImageUrl(img.src);
      }
    };
    img.src = src ? processImgSrc(src, downsize) : placeholder;

    return () => {
      isMounted = false;
      img.onload = null;
    };
  }, [src, image, imageType, downsize, placeholder]);

  const getImgSrc = useCallback(() => {
    if (isBase64InSrc) {
      return src?.length && imageType === ProductImageTypes.base64
        ? `data:image/${imageExtension};base64, ${src}`
        : imageUrl;
    }

    const base64Src = isNeedBase65Prefix ? `data:image/${imageExtension};base64,` : "";

    const imgSrc = src?.length && imageType === ProductImageTypes.base64 ? `${base64Src}${image?.base64}` : imageUrl;

    return imgSrc;
  }, [image?.base64, imageType, imageUrl, src, imageExtension, isNeedBase65Prefix, isBase64InSrc]);

  const isImgDefaultPlaceholder =
    placeholder === defaultPlaceholder && (!src || src === "" || imageUrl === placeholder);

  return lazyLoad ? (
    <IonImg
      draggable={false}
      src={getImgSrc()}
      className={cx(className, "Image", isImgDefaultPlaceholder && placeholderClassName)}
      onError={handleImgError}
      {...imgProps}
    />
  ) : (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      draggable={false}
      src={getImgSrc()}
      className={cx(className, "Image", isImgDefaultPlaceholder && placeholderClassName)}
      onError={handleImgError}
      {...imgProps}
    />
  );
};

export { Image };
